// 1729 — English UI strings (primary language)

const en = {
  giftWord: 'gift',

  auth: {
    signup: 'Sign up',
    login: 'Log in',
    email: 'Email',
    password: 'Password',
    processing: 'Processing',
    error: 'Something went wrong. Want to try again?',
    forgotPassword: 'Forgot password?',
    // Reset-password page
    resetTitle: 'Set a new password',
    resetSubtitle: 'Choose a new password for your account.',
    newPassword: 'New password',
    confirmPassword: 'Confirm password',
    updatePassword: 'Update password',
    passwordUpdated: 'Password updated. You can log in now.',
    passwordMismatch: 'Passwords don\'t match.',
    resetExpired: 'This link has expired or is invalid. Please request a new reset.',
    backToLogin: 'Back to login',
    noAccount: "Don't have an account? Sign up",
    haveAccount: 'Already have an account? Log in',
    signupInfo: 'Your account was created. We sent a confirmation link to your email. Click it, then log in.',
  },

  chat: {
    requestTitle: 'Write your request',
    requestHint: 'You do not need to introduce yourself. But a sentence or two on what this means to you and what you would use it for helps the giver decide with an easy heart.',
    requestPlaceholder: 'Hello, I am interested in this gift. I would use it for: ',
    askHint: 'If you are curious about something before giving, you can ask:',
    askQuestions: ['What will you use it for?', 'What does this mean to you?', 'How would it help you?'],
    send: 'Send',
    sending: 'Sending',
    placeholder: 'Write your message',
    empty: 'No messages yet.',
    close: 'Close',
    closedNote: 'This conversation was closed.',
    markReceived: 'Mark as received',
    deliveredNote: 'Delivered.',
    addToCircle: 'Add to trust circle',
    notFound: 'This conversation was not found or is not open to you.',
    error: 'Something went wrong. Want to try again?',
    inboxTitle: 'Messages',
    inboxEmpty: 'No new messages.',
    inboxAll: 'All conversations',
    listTitle: 'Conversations',
    listEmpty: 'No conversations yet.',
    unread: (n) => `${n} new`,
    open: 'Chat',
  },

  member: {
    back: 'Back',
    notVisible: 'This profile is not open to you. Profiles are only visible to people in the same community or trust circle.',
    you: 'This is you',
    gave: 'Gave',
    received: 'Received',
    circle: 'People who have them in their circle',
    times: (n) => `${n} ${n === 1 ? 'time' : 'times'}`,
    people: (n) => `${n} ${n === 1 ? 'person' : 'people'}`,
    gifts: 'Gifts',
    noGifts: 'No gifts live right now.',
    receivedTitle: 'Received',
    noReceived: 'Has not received anything yet.',
    answers: 'About them',
    aboutMe: 'About me',
    noAnswers: 'No answers.',
    openProfile: 'View profile',
    whyGive: 'Why do they give this?',
    request: 'Request',
    requesting: 'Sending',
    requested: 'Your request was sent.',
    ownGift: 'This is your gift.',
    notFound: 'This gift was not found or is not open to you.',
    error: 'Something went wrong. Want to try again?',
  },

  disaster: {
    tab: 'Disaster',
    offerButton: 'Offer for a disaster',
    modalTitle: 'What could you offer in a disaster?',
    intro: 'Share something you could do to help others in a disaster. Every member can see it, and it stays up for 7 days.',
    categoryLabel: 'What do you offer?',
    categories: {
      housing: 'Shelter',
      food: 'Food and water',
      transport: 'Transport',
      health: 'Health knowledge',
      communication: 'Communication',
      shipping: 'Shipping',
      other: 'Other',
    },
    descLabel: 'Details',
    descPlaceholder: 'e.g. Around Kadikoy, Istanbul, I can offer temporary shelter to two people. Available on weekends.',
    descHint: 'Write which area it is for, what you can offer and when you are available. Do not share a phone number or address; contact happens through the in-site chat.',
    minChars: (n, now) => `Write at least ${n} characters (now ${now}).`,
    publish: 'Publish',
    badge: 'Disaster',
    daysLeft: (d) => (d > 0 ? `${d} days left` : 'Expired'),
    renew: 'Extend 7 days',
    report: 'Report',
    reported: 'Reported',
    empty: 'There are no disaster offers right now.',
  },

  community: {
    homePage: 'Home page',
    skip: 'Not now, let me look around first',
    skipHint: 'You can send invitations later from the Trust Circle page.',
    defaultName: 'My community',
    mailSentNote: 'Invitation emails were sent. A summary with the links was also emailed to you.',
    setupTitle: 'Found your community',
    setupSubtitle: 'Everyone here belongs to a community. Name yours and invite at least two people you trust.',
    nameLabel: 'Community name',
    namePlaceholder: 'e.g. Neighbourhood, Old Friends',
    durationLabel: 'Access length for invitees',
    months6: '6 months',
    months12: '12 months',
    emailsLabel: 'Emails of the people you invite',
    emailsPlaceholder: 'name1@example.com\nname2@example.com',
    emailsHint: (n) => n >= 2 ? `${n} people will be invited.` : `At least 2 valid emails needed (currently ${n}). One per line or comma-separated.`,
    invalidEmails: (list) => `Invalid address: ${list}`,
    create: 'Create community and send invites',
    creating: 'Creating',
    needTwo: 'Enter at least 2 valid emails.',
    partialFail: 'Some invitations could not be created. At least 2 are needed. Try again.',
    error: 'Something went wrong. Want to try again?',
    mailNote: "If an invite email couldn't be sent, copy the links below and share them yourself.",
    done: 'Invitations are ready.',
    copyAll: 'Copy links',
    copied: 'Copied!',
    continue: 'Continue',
    myCommunities: 'My communities',
    founder: 'Founder',
    member: 'Member',
    unnamed: 'Unnamed community',
    scoreboard: 'Table',
    sbTitle: 'Community table',
    sbBack: 'Back',
    sbWeek: 'This week',
    sbMonth: 'This month',
    sbYear: 'This year',
    sbAll: 'All time',
    sbPerson: 'Member',
    sbGiven: 'Gave',
    sbReceived: 'Received',
    sbOffers: 'Open cards',
    sbOpen: 'Let members see it too',
    sbOpenHint: 'While off, only you can see it.',
    sbClosed: 'The founder has not opened this table.',
    sbEmpty: 'Nobody here yet.',
    sbNote: 'Only gifts marked “Received” are counted.',
    newCommunity: '+ Found a new community',
    newTitle: 'New community',
    save: 'Create',
    cancel: 'Cancel',
    empty: "You don't belong to a community yet.",
  },

  onboarding: {
    screen1: { headline: 'No name here. No title. No history. Just you.', cta: 'Ready' },
    screenVillage: {
      line1: 'Welcome.',
      line2: '1. You will be given a colour. That is your identity here. How much of yourself you share is entirely up to you: as much as you like, or nothing at all.',
      line3: '2. We will ask you one question: what can you give? A gift card will be made from your answer, and that is how you join the system. If you want deeper questions, they come later, from your home page.',
      line4: '3. If you came by invitation, you are part of a community. You share gifts and get in touch only with the people in that community.',
      line5: '4. If you like, you can also found your own community and build a closed circle of giving.',
      cta: 'Start',
    },
    screen2: {
      question: "Think of a recent moment when you lost track of time. What were you doing?",
    },
    screen3: {
      question: "Your friends come to you first about one thing. What is it?",
    },
    screen4: {
      question: "What is the one job where you would most like someone to lend you a hand?",
    },
    screen6: {
      explanation: 'Now we will ask what you can give to your community. You are in a small community and everyone puts something forward. One day someone comes to you and needs your help with something. You can do it easily, even gladly. That is what you give to the community.\n\nYour gift card will be created from your answer. You can edit it whenever you like and add as many new gift cards as you want.',
      question: 'What would you be glad to give your community, something that feels good to you as well?',
      optionalLabel: 'Optional',
      optionalPlaceholder: 'Why you, and not someone else?',
      cta: 'Create my gift',
    },
    screen7: { ready: 'Your gift is ready. This is how your community will see it.', cta: 'Continue', edit: 'Edit' },
    giftQuestion: 'What can you give?',
    giftIntro: 'An object, an hour of your time, something you know: anything you have works. One sentence is enough. You can edit the card whenever you like.',
    skipGift: 'Skip for now, let me look around first',
    saveError: 'Your gift card could not be created. Want to try again?',
    saving: 'Saving',
    textareaPlaceholder: 'Write here.',
  },

  nav: {
    dashboard: 'Home',
    trustTeam: 'Trust Circle',
    explore: 'Explore',
    give: 'Give',
    settings: 'Settings',
    signOut: 'Sign out',
  },

  tour: {
    label: 'Introduction',
    next: 'Next',
    back: 'Back',
    skip: 'Skip',
    done: 'Start',
    replay: 'Show the introduction again',
    steps: [
      { title: 'Welcome', body: 'There are no names here. Your colour, the code you see, is the only thing others know you by. Only people in your community can see you.', target: null },
      { title: 'Give', body: 'Write what you can give in one sentence: an object, an hour of your time, something you know. Nothing is expected in return.', target: '/give' },
      { title: 'Explore', body: 'Browse what people in your community are giving. If something fits, press "Request" and a conversation opens.', target: '/receive' },
      { title: 'Conversations', body: 'When someone asks for a gift or writes to you, you will see it at the envelope icon, top right. Once it reaches you, press "Received" and it counts.', target: 'inbox' },
      { title: 'Trust Circle', body: 'Invite the people you know from here. After receiving a gift, you can also add the giver to your circle if you want to.', target: '/trust-team' },
    ],
  },

  dashboard: {
    firstGiftTitle: 'You have no gift yet',
    firstGiftBody: 'Adding your first takes one sentence. When others see you give, they start giving too.',
    firstGiftCta: 'Add your first gift',
    knowTitle: 'If you like, tell us a little about yourself',
    knowBody: 'Three short questions. Answer as much as you want, and people in your community get to know you a bit better from your profile.',
    knowCta: 'Tell about yourself',
    nickAdd: 'Add a nickname',
    nickEdit: 'Change',
    nickSave: 'Save',
    nickCancel: 'Cancel',
    nickPlaceholder: 'your nickname',
    nickHint: 'Optional. Your colour is always shown, and your nickname appears beside it in some places. Everyone in your community can see it.',
    nickError: 'Could not save. Please try again in a moment.',
    hexChange: 'Change colour',
    hexChangeHint: 'You can change your colour until you offer your first gift. After that it stays.',
    hexApply: 'Use this one',
    hexAnother: 'Another one',
    hexError: 'Could not change it. Try another colour.',
    knowLater: 'Not now',
    activeCards: 'Active gifts',
    supportGiven: 'Gifts given',
    supportReceived: 'Gifts received',
    trustTeamSize: 'People who have you in their circle',
    recentActivity: 'Recent activity',
    noActivity: 'Nothing here yet.',
    gave: 'You gave a gift.',
    received: 'You received a gift.',
    quickActions: {
      createCard: 'New gift',
      createCardDesc: 'Create a new gift card.',
      explore: 'Explore',
      exploreDesc: 'Browse gifts from your circle and beyond.',
      editQuestions: 'Edit questions',
      editQuestionsDesc: 'Update your answers.',
      trustTeam: 'Trust Circle',
      trustTeamDesc: 'Manage your circle and invitations.',
    },
  },

  give: {
    iconAuto: 'Picked automatically',
    iconChange: 'Change',
    iconClose: 'Close',
    title: 'Give',
    subtitle: 'Manage your gift cards.',
    tabMy: 'My gifts',
    tabHistory: 'Given',
    cardActive: 'Active',
    cardInactive: 'Paused',
    newCard: 'New gift',
    newCardTitle: 'New gift',
    edit: 'Edit',
    editTitle: 'Edit gift',
    save: 'Save',
    statusLive: 'Live',
    statusPaused: 'Paused',
    visibleTo: 'Visible to',
    pause: 'Pause',
    resume: 'Make live',
    offerQuestion: 'What can you give?',
    offerPlaceholder: 'For example: 30 minutes for better coffee at home',
    offerHint: 'An object, an hour, some knowledge: anything you have works. One or two sentences are enough.',
    examples: [
      { label: 'Coffee lesson', text: '30 minutes for better coffee at home' },
      { label: 'A pot', text: 'I can give away a pot I no longer use' },
      { label: 'Look over your contract', text: 'I will look over your contract before you sign' },
    ],
    addGift: 'Add gift',
    moreOptions: 'Edit details',
    lessOptions: 'Hide details',
    titleAuto: 'Made automatically from what you write',
    titleLabel: 'Title',
    titleHint: 'Describe what you give in one short, concrete sentence. Whoever reads the title should understand what they would receive. Examples: "30 minutes for better coffee at home", "A look at your contract before you sign", "Bike maintenance".',
    titlePlaceholder: 'e.g. 30 minutes for better coffee at home',
    descLabel: 'Description',
    descHint: 'Write what you will give and how: how long, the format (in person or online), who it suits. You do not need to introduce yourself, just describe what you give.',
    descPlaceholder: 'e.g. I love brewing coffee. We can try ways to make better coffee with what you have at home. Online or in person.',
    // Gift type
    giftTypeLabel: 'Type',
    typeUnlimited: 'Always open',
    typeUnlimitedHint: 'No limit, always available.',
    typeQuota: 'Limited',
    typeQuotaHint: 'A set number of uses.',
    typeOnce: 'One-time',
    typeOnceHint: 'One person takes it, then it closes.',
    quotaLabel: 'How many?',
    // Physical
    physicalLabel: 'Format',
    digital: 'Digital',
    physical: 'Physical',
    deliveryNoteLabel: 'Delivery note',
    deliveryNotePlaceholder: 'Anything about shipping or handoff? (optional)',
    deliveryNoteHint: 'Logistics are handled between you two. 1729 doesn\'t coordinate.',
    // Visibility
    visibilityLabel: 'Visibility',
    visTrustCircle: 'Trust Circle',
    visCommunity: 'Community',
    visGlobal: 'Global',
    comingSoon: 'Coming soon',
    // Other
    whyMeLabel: 'Why do you give this?',
    whyMeHint: 'Write what giving this means to you. You do not need to compare yourself with anyone, just describe its value to you.',
    whyMePlaceholder: 'What does giving this mean to you, and how does it feel?',
    optional: 'optional',
    create: 'Create',
    cancel: 'Cancel',
    creating: 'Creating',
    empty: 'No gifts yet.',
    historyEmpty: 'You haven\'t given any gifts yet.',
    error: 'Something went wrong. Want to try again?',
    // Card badges
    badgeUnlimited: 'Always open',
    badgeOnce: 'One-time',
    badgeQuota: (n) => `${n} left`,
    badgePhysical: 'Physical',
    interested: (n) => n === 1 ? '1 request' : `${n} requests`,
  },

  explore: {
    newBadge: 'New',
    title: 'Explore',
    subtitle: 'Browse gifts from your circle and beyond.',
    tabFeed: 'Gifts',
    tabHistory: 'Received',
    filterTrustCircle: 'Trust Circle',
    filterCommunity: 'Community',
    filterGlobal: 'Global',
    filterAll: 'All',
    filterUnlimited: 'Always open',
    filterQuota: 'Limited',
    filterOnce: 'One-time',
    filterPhysical: 'Physical',
    requestSupport: 'Request',
    requesting: 'Sending',
    empty: 'Nothing here yet.',
    historyEmpty: 'You haven\'t received any gifts yet.',
    error: 'Something went wrong. Want to try again?',
    markReceived: 'Mark as received',
    addToCircle: 'Add to your circle?',
    addToCircleHint: 'You received a gift from this person. Would you like to add them to your trust circle?',
    addToCircleYes: 'Add',
    addToCircleSkip: 'Skip',
    thanksLabel: 'Thank-you note (optional)',
    thanksPlaceholder: 'How did this gift help you?',
    thanksHint: 'A short note is enough. It appears on the gift page, visible to the community.',
    thanksHeading: 'Thank-you notes',
    thanksHide: 'Hide',
    thanksShow: 'Show',
    thanksHiddenTag: 'hidden',
    added: 'Added.',
    comingSoon: 'Coming soon',
    // Filter row
    typeLabel: 'Type',
    // Empty-state card
    emptyCircleTitle: 'This circle is empty.',
    emptyCircleText: 'Offer the first gift. The circle comes alive when you give.',
    emptyOtherTitle: 'No gifts here yet.',
    emptyOtherText: 'Be the first to offer one.',
    offerGift: 'Offer a gift',
  },

  questions: {
    title: 'About me',
    subtitle: 'There is no name here. But if you like, the people in your community can get to know you a little: how much you open up is entirely up to you. Answer what you want and leave the rest blank. People in the same community see your answers on your colour\'s profile.',
    save: 'Save',
    saving: 'Saving',
    saved: 'Saved.',
    createCardLabel: 'Create a gift card from this answer.',
    error: 'Something went wrong. Want to try again?',
  },

  trustTeam: {
    title: 'Trust Circle',
    subtitle: 'Your trust circle and the people you\'ve invited.',
    tabCircle: 'Trust Circle',
    tabInvited: 'Invited',
    invite: 'Invite',
    emptyCircle: 'Your trust circle is empty.',
    emptyCircleHint: 'After receiving a gift, mark it as received to add that person here.',
    emptyInvited: 'You haven\'t invited anyone yet.',
    subscriptionActive: 'Active',
    subscriptionEnded: 'Ended',
    subscriptionPending: 'Pending',
    daysLeft: (n) => `${n}d left`,
    inCommunity: 'Community',
    alreadyInvited: 'Already invited',
    copyLinkShort: 'Copy link',
    resend: 'Resend',
    sentShort: 'Sent',
    sendFailed: 'Could not send',
    endSubscription: 'End',
    gaveGift: 'gave you a gift',
    modal: {
      title: 'Invite someone',
      step1: 'Their email',
      step2: 'Duration',
      step3: 'Community',
      step4: 'Invite',
      emailLabel: 'Email address',
      emailPlaceholder: 'name@example.com',
      duration1: '1 month',
      duration6: '6 months',
      duration12: '12 months',
      communityNew: 'New community',
      communityGeneral: 'General (no community)',
      communityExisting: 'Existing community',
      communityNameLabel: 'Community name',
      membersAwareLabel: 'Members will know they\'re in a named group',
      next: 'Next',
      back: 'Back',
      generate: 'Create invite',
      linkReady: 'Share the invite link',
      linkHint: 'They join by opening this link and setting a password.',
      codeBackupLabel: 'Backup code',
      copyLink: 'Copy link',
      copy: 'Copy',
      copied: 'Copied!',
      close: 'Done',
      // Bulk invite
      bulkLabel: 'Emails (one per line)',
      bulkPlaceholder: 'name1@example.com\nname2@example.com\nname3@example.com',
      bulkSwitchMany: 'Multiple at once',
      bulkSwitchSingle: 'Single person',
      bulkHint: (n) => `${n} ${n === 1 ? 'person' : 'people'} will be invited.`,
      bulkSubject: (n) => `${n} ${n === 1 ? 'person' : 'people'}`,
      bulkReady: (n) => `${n} ${n === 1 ? 'invite' : 'invites'} ready`,
      sentNote: 'Invite emails were sent. You can also share the links yourself.',
      copyAll: 'Copy all',
    },
  },

  communityLink: {
    title: 'Join link',
    hint: 'Anyone who opens the link can create an account and join this community. The link does not show who made it. You can switch it off or renew it whenever you like.',
    create: 'Create link',
    copy: 'Copy',
    copied: 'Copied',
    on: 'Switch on',
    off: 'Switch off',
    renew: 'Renew',
    renewConfirm: 'The old link will stop working. Renew it?',
    isOff: 'The link is switched off.',
    error: 'Something went wrong. Want to try again?',
  },

  joinCommunity: {
    title: (name) => `Join ${name}`,
    subtitle: 'You came with an invitation link. No names here, everyone is a colour.',
    signup: 'Create an account and join',
    login: 'I already have an account',
    join: 'Join',
    joining: 'Joining',
    invalid: 'This link is not valid or has been switched off.',
    home: 'Home',
    error: 'Something went wrong. Want to try again?',
  },

  about: {
    what: 'What is this? Read the manifesto',
    footerHome: 'What is 1729?',
    footerManifesto: 'Manifesto',
    footerPricing: 'Pricing',
    footerContact: 'Contact',
  },

  announce: {
    title: 'Announcements',
    new: 'new',
    viewAll: 'See all',
    composeTitle: 'Write an announcement',
    hint: 'It goes to everyone in your community. Nobody sees who wrote it. One announcement a day at most.',
    hintSystem: 'It goes to everyone, signed #1729.',
    placeholder: 'Write your announcement',
    send: 'Send',
    sending: 'Sending',
    alsoEmail: 'Also send as an e-mail (one a week at most)',
    alsoEmailSystem: 'Important: also send as an e-mail',
    sent: 'Sent.',
    sentEmailOff: 'Announcement sent. E-mail is switched off for now, it shows on the site only.',
    sentEmailFailed: 'Announcement sent, but the e-mail could not be sent.',
    tooSoon: 'You already sent an announcement today. Try again tomorrow.',
    error: 'Something went wrong. Want to try again?',
  },

  join: {
    browserNote: "If you cannot sign in or the screen stays blank, open this link in Chrome or Safari.",
    title: 'Join 1729',
    subtitle: 'You\'ve been invited. Set your password to come in.',
    invitedAs: 'Invited email',
    passwordLabel: 'Set a password',
    passwordPlaceholder: '••••••••',
    cta: 'Join',
    joining: 'Joining',
    invalidInvite: 'This invite is invalid or has already been used.',
    confirmEmail: 'Your account was created. We sent a confirmation link to your email. Click it, then log in.',
    haveAccount: 'Already have an account? Log in',
    error: 'Something went wrong. Want to try again?',
  },

  payment: {
    title: 'Join 1729',
    subtitle: 'You need an invite code to continue.',
    codeLabel: 'Invite code',
    codePlaceholder: 'XXXXXX',
    apply: 'Apply',
    invalidCode: 'Invalid or already used code.',
    codeAccepted: 'Code accepted.',
    requireCode: 'An invite code is required.',
    continue: 'Continue',
    processing: 'Processing',
    testMode: 'Test mode: no real payment.',
    error: 'Something went wrong. Want to try again?',
  },

  settings: {
    title: 'Settings',
    subtitle: 'Manage your account and preferences.',

    // Appearance
    appearance: 'Appearance & Language',
    theme: 'Theme',
    themeDark: 'Dark',
    themeLight: 'Light',
    themeSystem: 'System',
    language: 'Language',
    giftWord: 'Gift word',
    giftWordDefault: 'Gift',
    giftWordAlt: 'Thing',
    giftWordNote: 'This is only visible to you. Others always see their own preference.',

    // Subscription
    subscription: 'Subscription',
    plan: 'Plan',
    startDate: 'Start date',
    endDate: 'End date',
    daysLeft: (n) => `${n} day${n === 1 ? '' : 's'} left`,
    type: 'Type',
    typeGifted: 'Gifted by someone',
    typeSelf: 'Self-subscribed',
    managePayment: 'Manage payment',
    comingSoon: 'Coming soon',

    // Notifications
    notifications: 'Email notifications',
    notifGiftInterest: 'Someone is interested in your gift',
    notifGiftInterestHint: 'When a conversation starts around your gift.',
    notifNewMessage: 'New message',
    notifNewMessageHint: 'When you receive a new message in an active conversation.',
    notifAnnouncements: 'Announcement e-mails',
    notifAnnouncementsHint: 'You also get announcements from your community or from 1729 by e-mail. If off, announcements only show on the site.',
    notifShowPreview: 'Show the message text in e-mails',
    notifShowPreviewHint: 'If off, the e-mail only says you have a new message and does not show the text.',
    notifSubExpiring: 'Subscription expiring soon',
    notifSubExpiringHint: '5 days before your access ends.',
    notifSubExpired: 'Subscription ended',
    notifSubExpiredHint: 'When your access expires.',
    notifAddedToCircle: 'Added to someone\'s circle',
    notifAddedToCircleHint: 'When someone adds you to their trust circle.',
    notifQuotaEmpty: 'Gift quota reached',
    notifQuotaEmptyHint: 'When a limited gift runs out of uses.',

    // Account
    account: 'Account',
    emailLabel: 'Email address',
    changePassword: 'Change password',
    newPassword: 'New password',
    updatePassword: 'Update',
    passwordUpdated: 'Password updated.',
    dangerZone: 'Danger zone',
    deleteAccount: 'Permanently delete your account and all data.',
    deleteConfirmPlaceholder: 'Type DELETE to confirm',
    deleteConfirmWord: 'DELETE',
    confirmDelete: 'Permanently delete',

    saved: 'Saved.',
    error: 'Something went wrong. Try again?',
  },

  language: { label: 'Language', en: 'English', tr: 'Türkçe' },

  errors: {
    generic: 'Something went wrong. Want to try again?',
    dbError: 'Connection failed.',
  },
}

export default en
